import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  Box,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Apartment,
  Handshake,
  ArrowBack,
  CreditCard,
} from '@mui/icons-material';
import { normalizePaidPlanType, PaidPlanType, onboardingService } from '../services/onboardingService';
import { Header } from '../../../components/layout/Header';
import { useAuth } from '../../../hooks/useAuth';
import { asaasService } from '../../../services/asaasService';
import { 
  Card, 
  CardBody, 
  Button, 
  Typography, 
  Heading1, 
  Heading2, 
  BodyText 
} from '../../../design-system';
import { DESIGN_TOKENS, SPACING_TOKENS } from '../../../design-system/tokens';

interface PlanDetails {
  key: PaidPlanType;
  name: string;
  price: string;
  period: string;
  description: string;
  idealFor: string;
  icon: React.ReactElement;
  color: string;
  isPopular?: boolean;
}

const PlanSelectionScreen: React.FC = () => {
  const { t } = useTranslation(['common', 'landing']);
  const navigate = useNavigate();
  const location = useLocation();
  const { authState } = useAuth();
  const isWebview = location.pathname.startsWith('/onboarding-webview');
  const [searchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState<PaidPlanType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [planPrice, setPlanPrice] = useState<string>('R$ 30,00');
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);
  const allowLeaveRef = useRef(false);
  const hasPushedGuardRef = useRef(false);
  const ignoreNextPopRef = useRef(false);
  const hasCheckedPendingSubscriptionRef = useRef(false);

  const guardEnabled = !isLoading;

  useEffect(() => {
    const planParam = normalizePaidPlanType(searchParams.get('plan'));
    if (planParam) {
      onboardingService.storePlanSelection(planParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!authState.isAuthenticated) return;
    const email = String(authState.user?.email || '').trim().toLowerCase();
    if (!email) return;
    if (hasCheckedPendingSubscriptionRef.current) return;
    hasCheckedPendingSubscriptionRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const access = await asaasService.checkAccessByEmail(email);
        if (cancelled) return;
        const hasPendingSubscription = Boolean((access as any)?.subscription) && !Boolean((access as any)?.accessGranted);
        if (!hasPendingSubscription) return;
        const pendingPlan = normalizePaidPlanType((access as any)?.planType)
          || normalizePaidPlanType(searchParams.get('plan'))
          || normalizePaidPlanType(onboardingService.getSelectedPlan())
          || 'producer';
        setIsLoading(true);
        allowLeaveRef.current = true;
        const paymentPath = isWebview ? '/onboarding-webview/payment' : '/payment';
        const nextParams = new URLSearchParams();
        nextParams.set('plan', pendingPlan);
        nextParams.set('email', email);
        navigate(`${paymentPath}?${nextParams.toString()}`, { replace: true, state: location.state });
      } catch (error) {
        Sentry.setTag('onboarding_step', 'plan_selection_pending_check');
        Sentry.setTag('is_authenticated', String(!!authState.isAuthenticated));
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authState.isAuthenticated, authState.user?.email, isWebview, navigate, location.state, searchParams]);

  useEffect(() => {
    if (!guardEnabled) return;
    if (hasPushedGuardRef.current) return;
    hasPushedGuardRef.current = true;
    window.history.pushState(null, '', window.location.href);
  }, [guardEnabled]);

  useEffect(() => {
    if (!guardEnabled) return;

    const onPopState = (event: PopStateEvent) => {
      if (allowLeaveRef.current) return;
      
      // Bloqueia a navegação voltando para a mesma URL
      window.history.pushState(null, '', window.location.href);
      setConfirmLeaveOpen(true);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [guardEnabled]);

  // Preço fixo (Asaas)

  const planDetails = useMemo<PlanDetails[]>(() => {
    return (['producer', 'professional'] as PaidPlanType[]).map((planKey) => {
      const fallbackName = planKey === 'professional' ? 'Profissional' : 'Produtor';
      const name = t(`landing:pricing.plans.${planKey}.name`, { defaultValue: fallbackName });
      const description = t(`landing:pricing.plans.${planKey}.description`, {
        defaultValue: planKey === 'professional'
          ? 'Acesso avançado para profissionais com múltiplas fazendas.'
          : 'Gestão completa para produtores rurais.',
      });
      const idealFor = t(`landing:pricing.plans.${planKey}.ideal_for`, {
        defaultValue: planKey === 'professional'
          ? 'Ideal para veterinários, consultores e técnicos.'
          : 'Ideal para produtores e gestores de fazenda.',
      });
      const period = t('common:time.month');
      const isProducer = planKey === 'producer';
      const icon = isProducer ? (
        <Apartment sx={{ fontSize: '2.5rem', color: DESIGN_TOKENS.colors.primary[600] }} />
      ) : (
        <Handshake sx={{ fontSize: '2.5rem', color: DESIGN_TOKENS.colors.secondary[600] }} />
      );
      const color = isProducer ? DESIGN_TOKENS.colors.primary[600] : DESIGN_TOKENS.colors.secondary[600];
      return {
        key: planKey,
        name,
        price: planPrice,
        period,
        description,
        idealFor,
        icon,
        color,
        isPopular: planKey === 'producer',
      };
    });
  }, [planPrice, t]);

  const planSelectionTitle = t('landing:onboarding.planSelection.title', {
    defaultValue: t('common:onboarding.planSelection.title', { defaultValue: 'Escolha seu plano' }),
  });
  const planSelectionSubtitle = t('landing:onboarding.planSelection.subtitle', {
    defaultValue: t('common:onboarding.planSelection.subtitle', { defaultValue: 'Selecione a solução ideal para o seu perfil' }),
  });

  const handleBackToPlans = () => {
    allowLeaveRef.current = true;
    navigate(isWebview ? '/onboarding-webview/signup' : '/#pricing-section', { replace: isWebview });
  };

  const handleSelectPlan = (plan: PaidPlanType) => {
    setSelectedPlan(plan);
    onboardingService.storePlanSelection(plan);
    setIsLoading(true);
    allowLeaveRef.current = true;
    const paymentPath = isWebview ? '/onboarding-webview/payment' : '/payment';
    const nextParams = new URLSearchParams();
    nextParams.set('plan', plan);
    const emailParam = searchParams.get('email') || String(authState.user?.email || '').trim().toLowerCase();
    if (emailParam) nextParams.set('email', emailParam);
    navigate(`${paymentPath}?${nextParams.toString()}`, { state: location.state });
  };

  return (
    <>
      {!isWebview && <Header progress={0} />}
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          px: { xs: 3, sm: 4 },
          py: { xs: 6, md: 10 },
          pt: isWebview ? { xs: 6, md: 8 } : { xs: '120px', sm: '104px', md: '88px' },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', md: 820, lg: 980 },
          }}
        >
          <Stack spacing={SPACING_TOKENS.semantic.lg} sx={{ width: '100%' }} alignItems="center">
            <Stack spacing={1} alignItems="center">
              <Heading1 sx={{ color: DESIGN_TOKENS.colors.text.primary, textAlign: 'center' }}>
                {planSelectionTitle}
              </Heading1>
              <BodyText sx={{ color: DESIGN_TOKENS.colors.text.secondary, textAlign: 'center' }}>
                {planSelectionSubtitle}
              </BodyText>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 3,
                width: '100%',
              }}
            >
              {planDetails.map((plan) => {
                const isSelected = selectedPlan === plan.key;
                return (
                    <Card
                    key={plan.key}
                    onClick={() => setSelectedPlan(plan.key)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedPlan(plan.key);
                      }
                    }}
                    sx={{
                      position: 'relative',
                      cursor: 'pointer',
                      outline: 'none',
                      border: `2px solid ${isSelected ? plan.color : DESIGN_TOKENS.colors.neutral[100]}`,
                      backgroundColor: isSelected ? DESIGN_TOKENS.colors.neutral[50] : DESIGN_TOKENS.colors.background.paper,
                      boxShadow: isSelected ? '0 20px 48px rgba(0,0,0,0.16)' : '0 8px 24px rgba(0,0,0,0.08)',
                      transform: isSelected ? 'scale(1.02)' : 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: isSelected ? 'scale(1.025)' : 'translateY(-2px)',
                        boxShadow: isSelected ? '0 22px 52px rgba(0,0,0,0.18)' : '0 18px 44px rgba(0,0,0,0.16)',
                      },
                      '&:focus-visible': {
                        boxShadow: `0 0 0 3px ${DESIGN_TOKENS.colors.primary[200]}`,
                      },
                    }}
                  >
                    {plan.isPopular && (
                      <Chip
                        label={t('landing:pricing.plans.producer.popular')}
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 16,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          zIndex: 2,
                        }}
                      />
                    )}
                    <CardBody sx={{ p: 3, minHeight: isSelected ? 380 : 360, transition: 'min-height 0.2s ease' }}>
                      <Stack spacing={2} alignItems="flex-start">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {plan.icon}
                          <Heading2 sx={{ color: DESIGN_TOKENS.colors.text.primary }}>{plan.name}</Heading2>
                        </Box>
                        <BodyText sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                          {plan.description}
                        </BodyText>
                        <BodyText sx={{ color: DESIGN_TOKENS.colors.text.tertiary }}>
                          {plan.idealFor}
                        </BodyText>
                        <Box>
                          {plan.key === 'professional' && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: DESIGN_TOKENS.colors.text.secondary,
                                mb: 0.5,
                                textDecoration: 'line-through',
                              }}
                            >
                              {t('landing:pricing.plans.professional.promo')}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: plan.color }}>
                              {plan.price}
                            </Typography>
                            <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                              / {plan.period}
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>
                    </CardBody>
                  </Card>
                );
              })}
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ width: '100%' }}>
              <Button
                variant="secondary"
                size="large"
                onClick={() => {
                  setConfirmLeaveOpen(true);
                }}
                disabled={isLoading}
                startIcon={<ArrowBack />}
                sx={{ minWidth: { xs: '100%', sm: '180px' }, height: '48px' }}
              >
                {t('common:buttons.back')}
              </Button>
              <Button
                variant="primary"
                size="large"
                onClick={() => {
                  if (!selectedPlan) return;
                  handleSelectPlan(selectedPlan);
                }}
                disabled={isLoading || !selectedPlan}
                loading={isLoading}
                startIcon={!isLoading ? <CreditCard /> : undefined}
                sx={{
                  minWidth: { xs: '100%', sm: '220px' },
                  height: '48px',
                  '&.Mui-disabled': {
                    backgroundColor: DESIGN_TOKENS.colors.neutral[200],
                    color: DESIGN_TOKENS.colors.text.disabled,
                  },
                }}
              >
                {t('common:buttons.proceed_to_payment')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
      <Dialog
        open={confirmLeaveOpen}
        onClose={() => setConfirmLeaveOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {t('payment.confirm_leave_plans_title')}
        </DialogTitle>
        <DialogContent sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
          {t('payment.confirm_leave_plans_desc')}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1.5 }}>
          <Button variant="secondary" size="small" onClick={() => setConfirmLeaveOpen(false)}>
            {t('payment.continue_plans')}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={() => {
              allowLeaveRef.current = true;
              setConfirmLeaveOpen(false);
              handleBackToPlans();
            }}
          >
            {t('payment.leave_plans')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlanSelectionScreen;
